import { SourcePreviewDescription } from "#features/conversations/components/conversation-source/source-preview/SourcePreviewDescription.tsx";
import { SourcePreviewFooter } from "#features/conversations/components/conversation-source/source-preview/SourcePreviewFooter.tsx";
import { SourcePreviewHeader } from "#features/conversations/components/conversation-source/source-preview/SourcePreviewHeader.tsx";
import type { SourcePreviewProps } from "#features/conversations/components/conversation-source/source-preview/SourcePreviewProps.ts";
import { useConversationSource } from "#features/conversations/hooks/useConversationSource.ts";
import { useSharedTranslation } from "#hooks/useSharedTranslation.ts";
import type { JiraIssueCustomProperties } from "#types/types.ts";
import { Div, Flex, Tag, type TagProps, Text } from "@hopper-ui/components";
import { useMemo } from "react";

type JiraStatusCategory = "new" | "inProgress" | "done";

interface StatusColors {
    backgroundColor: TagProps["backgroundColor"];
    color: TagProps["color"];
}

export const JiraIssuePreview = ({ source }: SourcePreviewProps) => {
    const { tShared } = useSharedTranslation();
    const { customProperties } = source;
    const { title, date, description } = useConversationSource(source);
    // Jira relays these from the issue itself, so any of them can be absent on a given issue type.
    const { issueKey, projectKey, projectName, issueType, status, assigneeDisplayName } =
        (customProperties ?? {}) as JiraIssueCustomProperties;

    // Status colors based on Jira's statusCategory system
    const statusColors = useMemo((): StatusColors => {
        // Map status names to their likely categories based on common Jira workflows
        const getStatusCategory = (statusName: string | null | undefined): JiraStatusCategory => {
            const normalizedStatus = statusName?.toLowerCase() ?? "";

            // "To Do" category statuses (gray/blue)
            if (normalizedStatus.includes("to do") ||
                normalizedStatus.includes("open") ||
                normalizedStatus.includes("new") ||
                normalizedStatus.includes("backlog") ||
                normalizedStatus.includes("created")) {
                return "new";
            }

            // "In Progress" category statuses (yellow/blue)
            if (normalizedStatus.includes("in progress") ||
                normalizedStatus.includes("progress") ||
                normalizedStatus.includes("development") ||
                normalizedStatus.includes("review") ||
                normalizedStatus.includes("testing") ||
                normalizedStatus.includes("pending")) {
                return "inProgress";
            }

            // "Done" category statuses (green)
            if (normalizedStatus.includes("done") ||
                normalizedStatus.includes("closed") ||
                normalizedStatus.includes("resolved") ||
                normalizedStatus.includes("complete") ||
                normalizedStatus.includes("finished") ||
                normalizedStatus.includes("rejected")) {
                return "done";
            }

            // Default to "new" for unknown statuses
            return "new";
        };

        const statusCategory = getStatusCategory(status);

        const statusColorMap: Record<JiraStatusCategory, StatusColors> = {
            new: {
                backgroundColor: "neutral-weak", // Gray for "To Do" category
                color: "decorative-option1"
            },
            inProgress: {
                backgroundColor: "decorative-option1-strong", // Blue for "In Progress" category
                color: "decorative-option1"
            },
            done: {
                backgroundColor: "decorative-option2-strong", // Green for "Done" category
                color: "decorative-option2"
            }
        };

        return statusColorMap[statusCategory];
    }, [status]);

    const issueTypeIcon = useMemo(() => {
        const typeIconMap: Record<string, string> = {
            "Bug": "🐛",
            "Story": "📖",
            "Epic": "⚡",
            "Improvement": "⬆️"
        };

        return typeIconMap[issueType] || "☑️";
    }, [issueType]);

    return (
        <div className="wai-source-card">
            <SourcePreviewHeader source={source} date={date} />
            <Flex
                direction="column"
                justifyContent="end"
                UNSAFE_marginTop="0.625rem"
                UNSAFE_minHeight="5.625rem"
                borderRadius="rounded-sm"
                UNSAFE_backgroundColor="#0052CC"
                paddingX="inset-md"
                paddingTop="inset-md"
                paddingBottom="inset-sm"
                backgroundImage="radial-gradient(farthest-corner at top right,rgba(255,255,255,.15) 25%,rgba(255,255,255,0))"
                position="relative"
            >
                <Flex
                    direction="column"
                    gap="inline-sm"
                    position="relative"
                    zIndex={1}
                >
                    {/* Issue Key and Status Row */}
                    <Flex
                        alignItems="center"
                        justifyContent="space-between"
                        wrap="wrap"
                        gap="inline-sm"
                    >
                        <Div
                            fontSize="body-xs-medium"
                            fontFamily="body-xs-medium"
                            fontWeight="body-xs-medium"
                            color="core_samoyed"
                            UNSAFE_backgroundColor="rgba(255, 255, 255, 0.25)"
                            paddingX="inset-sm"
                            paddingY="inset-xs"
                            borderRadius="rounded-sm"
                        >
                            {issueKey}
                        </Div>
                        <Tag
                            size="sm"
                            backgroundColor={statusColors.backgroundColor}
                            color={statusColors.color}
                            border="none"
                            UNSAFE_minHeight="1.5rem"
                        >
                            {status}
                        </Tag>
                    </Flex>

                    {/* Issue Title */}
                    <Div
                        fontSize="body-sm-semibold"
                        fontFamily="body-sm-semibold"
                        fontWeight="body-sm-semibold"
                        UNSAFE_lineHeight="1.25rem"
                        color="core_samoyed"
                    >
                        {title}
                    </Div>

                    {/* Project and Type Info */}
                    <Flex
                        direction="column"
                        gap="inline-xs"
                        marginTop="inline-sm"
                    >
                        <Div
                            fontSize="body-xs"
                            color="core_samoyed"
                            opacity="0.85"
                        >
                            {projectName} ({projectKey})
                        </Div>

                        <Flex
                            alignItems="center"
                            justifyContent="space-between"
                            wrap="wrap"
                            gap="inline-sm"
                        >
                            <Flex
                                alignItems="center"
                                gap="inline-xs"
                            >
                                <Text fontSize="body-xs" color="core_samoyed" opacity="0.85">
                                    {issueTypeIcon}
                                </Text>
                                <Div
                                    fontSize="body-xs"
                                    color="core_samoyed"
                                    opacity="0.85"
                                >
                                    {issueType}
                                </Div>
                            </Flex>

                            <Flex
                                alignItems="center"
                                gap="inline-xs"
                            >
                                <Text fontSize="body-xs" color="core_samoyed" opacity="0.85">
                                    👤
                                </Text>
                                <Div
                                    fontSize="body-xs"
                                    color="core_samoyed"
                                    opacity="0.85"
                                >
                                    {assigneeDisplayName ?? tShared("jiraUnassigned")}
                                </Div>
                            </Flex>
                        </Flex>
                    </Flex>
                </Flex>
            </Flex>

            {description && <SourcePreviewDescription description={description} />}
            <SourcePreviewFooter source={source} />
        </div>
    );
};
